import React, { useEffect, useMemo, useState } from "react";
import { get } from "lodash";

import {
  AbsoluteWrapper,
  Button,
  DatePicker,
  Dropdown,
  Input,
} from "../../kaya";
import { ICON_TYPES } from "../../kaya/icons";
import { generalConstants } from "../../constants";
import { authHelpers, generalHelpers, toasterHelpers } from "../../helpers";
import userStore from "../../zustand/user.store";
import { userActions } from "../../actions";
import { updateUserInIndexDB } from "../../helpers/indexDb.helpers";

import styles from "./PersonalDetails.module.scss";
import { personalOccupations } from "../../constants/general.constants";

const PersonalDetails = () => {
  const { user, setUser, userOffline } = userStore((state) => ({
    user: state.user,
    setUser: state.setUser,
    userOffline: state.userOffline,
  }));
  const [formValues, setFormValues] = useState({
    name: "",
    username: "",
    country: "",
    birthday: "",
    national_identity_no: "",
    phone_no: "",
    address: "",
    city: "",
    occupation: "",
    other: "",
  });
  const [loading, setLoading] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = React.useState(false);

  const [err, setErr] = React.useState({
    name: "",
    national_identity_no: "",
    stage_name: "",
    birthday: "",
    address: "",
    city: "",
    occupation: "",
    other: "",
    country: "",
  });

  const handleChange = (id) => (value) => {
    if (typeof id !== "object") {
      setFormValues({
        ...formValues,
        [id]: value,
      });
      setErr({
        ...err,
        [id]: "",
      });
    } else {
      setFormValues({
        ...formValues,
        ...id,
      });
      setErr({
        ...err,
        ...id,
      });
    }
  };

  const handleSubmit = async () => {
    if (userOffline)
      return toasterHelpers.showToaster(
        "error",
        "You are offline, please check your internet connection"
      );
    const error = {
      name: authHelpers.validateName(get(formValues, "name")),
      national_identity_no: authHelpers.validateNationalNo(
        get(formValues, "national_identity_no", "")
      ),
      birthday: authHelpers.validateDate(get(formValues, "birthday")),
      address: authHelpers.validateAddress(get(formValues, "address")),
      city: authHelpers.validateCity(get(formValues, "city")),
      occupation: authHelpers.validateOccupation(
        get(formValues, "occupation.value", "")
      ),
      other:
        get(formValues, "occupation.value", "") === "Other"
          ? authHelpers.otherOption(get(formValues, "other"))
          : "",
      country: authHelpers.validateCountry(
        user.country
          ? get(user, "country", "")
          : get(formValues, "country.value", "")
      ),
    };

    if (!generalHelpers.isEmptyErrors(error)) {
      setErr({
        ...err,
        ...error,
      });
      return;
    }
    setLoading(true);

    //api call
    const data = await userActions.updateUserAction(
      {
        first_name: get(formValues, "name").split(" ")[0],
        last_name: get(formValues, "name").split(" ")[1],
        birthday: generalHelpers.formatDateAccordingToDateType(
          get(formValues, "birthday", ""),
          "mm/dd/yyyy"
        ),
        address: get(formValues, "address"),
        city: get(formValues, "city"),
        occupation:
          get(formValues, "occupation.value") === "Other"
            ? get(formValues, "other")
            : get(formValues, "occupation.value"),
        country: get(formValues, "country.value"),
        national_identity_no: get(formValues, "national_identity_no"),
      },
      get(user, "id"),
      userOffline
    );
    if (data.success) {
      setUser(data.data);
      updateUserInIndexDB(data.data, "user-data");

      setLoading(false);
    }

    setLoading(false);
  };

  const occupationExists = useMemo(() => {
    return personalOccupations.some(
      (occupation) => occupation.value === user.occupation
    );
  }, [user.occupation]);

  useEffect(() => {
    setFormValues((prevFormValues) => {
      const newFormValues = {
        ...prevFormValues,
        name: `${get(user, "first_name", "")} ${get(user, "last_name", "")}`,
        birthday: user.birthday ? new Date(user.birthday) : "",
        address: get(user, "address"),
        city: get(user, "city"),
        occupation: occupationExists
          ? { value: user.occupation, label: user.occupation }
          : { value: "Other", label: "Other" },
        other: occupationExists ? "" : user.occupation,
        country: get(user, "country"),
        national_identity_no: get(user, "national_identity_no"),
      };

      // Only update form values if they have changed
      if (JSON.stringify(prevFormValues) !== JSON.stringify(newFormValues)) {
        return newFormValues;
      }
      return prevFormValues;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, occupationExists]);

  const disableButton = () => {
    let isDate = false;
    if (user.birthday) {
      isDate =
        new Date(formValues?.birthday)?.getTime() ===
        new Date(user.birthday).getTime();
    }

    const isOccupationOther = get(user, "occupation") === "Other";
    const isOtherFieldEqual = isOccupationOther
      ? get(formValues, "other") === get(user, "other")
      : true;

    if (
      formValues.name ===
        `${get(user, "first_name", "")} ${get(user, "last_name", "")}` &&
      isDate &&
      formValues.address === get(user, "address") &&
      formValues.city === get(user, "city") &&
      formValues.occupation === get(user, "occupation") &&
      isOtherFieldEqual &&
      formValues.country === get(user, "country") &&
      formValues.national_identity_no === get(user, "national_identity_no")
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className={styles.PersonalDetails}>
      <div className={styles.box}>
        <div className={styles.labelWithinput}>
          <p className={styles.label}>Username</p>

          <Input
            placeholder="Username"
            containerClassName={styles.input}
            value={get(user, "username", "")}
            disabled
          />
        </div>
        <div className={styles.labelWithinput}>
          <p className={styles.label}>Full Name</p>

          <Input
            placeholder="Full Name"
            containerClassName={styles.input}
            value={get(formValues, "name", "")}
            onChange={handleChange("name")}
            id="name"
            errors={err}
          />
        </div>
        <div className={styles.labelWithinput}>
          <p className={styles.label}>Email</p>

          <Input
            placeholder="Email"
            containerClassName={styles.input}
            value={get(user, "email", "")}
            disabled
          />
        </div>
        <div className={styles.labelWithinput}>
          <p className={styles.label}>Birthday</p>

          <AbsoluteWrapper
            componentToTrigger={
              <Input
                containerClassName={styles.input}
                placeholder="Birthday"
                // onChange={() => {
                //   setVisible(true);
                // }}
                value={
                  formValues.birthday &&
                  generalHelpers.formatDateAccordingToDateType(
                    formValues.birthday
                  )
                }
                suffixIcon={ICON_TYPES.CALENDAR}
                width="560px"
                errors={err}
                id="birthday"
                // onClick={() => {
                //   setDatePickerVisible(true);
                // }}
              />
            }
            placement="bottomLeft"
          >
            <DatePicker
              setDate={handleChange("birthday")}
              date={!formValues.birthday ? new Date() : formValues.birthday}
              visible={datePickerVisible}
              setVisible={setDatePickerVisible}
            />
          </AbsoluteWrapper>
        </div>
        <div className={styles.labelWithinput}>
          <p className={styles.label}>Phone No</p>

          <Input
            placeholder="Phone No."
            containerClassName={styles.input}
            value={get(user, "phone_no", "").toString()}
            disabled
          />
        </div>

        <div className={styles.labelWithinput}>
          <p className={styles.label}>Address</p>
          <Input
            placeholder="Address"
            containerClassName={styles.input}
            value={get(formValues, "address", "")}
            onChange={handleChange("address")}
            id="address"
            errors={err}
          />
        </div>

        <div className={styles.labelWithinput}>
          <p className={styles.label}>City</p>
          <Input
            placeholder="City"
            containerClassName={styles.input}
            value={get(formValues, "city", "")}
            onChange={handleChange("city")}
            id="city"
            errors={err}
          />
        </div>

        <div className={styles.labelWithinput}>
          <p className={styles.label}>Country</p>
          <Dropdown
            className={styles.input}
            placeholder="Country"
            options={generalConstants.countries}
            value={get(formValues, "country", "")}
            errors={err}
            id="country"
            onMenuItemClick={handleChange("country")}
          />
        </div>

        <div className={styles.labelWithinput}>
          <p className={styles.label}>Occupation</p>
          <Dropdown
            className={styles.input}
            placeholder="Occupation"
            options={generalConstants.personalOccupations}
            value={get(formValues, "occupation", "")}
            errors={err}
            id="occupation"
            onMenuItemClick={handleChange("occupation")}
          />
        </div>

        {formValues.occupation?.value === "Other" && (
          <div className={styles.labelWithinput}>
            <p className={styles.label}>Other</p>
            <Input
              placeholder="Occupation"
              containerClassName={styles.input}
              value={get(formValues, "other", "")}
              onChange={handleChange("other")}
              id="other"
              errors={err}
            />
          </div>
        )}

        <div className={styles.labelWithinput}>
          <p className={styles.label}>National Identity No.</p>

          <Input
            placeholder="National Identity No.."
            containerClassName={styles.input}
            value={get(formValues, "national_identity_no", "").toString()}
            onChange={handleChange("national_identity_no")}
            id="national_identity_no"
            errors={err}
            maxLength={9}
          />
        </div>
        <div className={styles.btnContainer}>
          <Button
            type="Primary"
            className={styles.btn}
            onClick={handleSubmit}
            disabled={disableButton()}
            isLoading={loading}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PersonalDetails;
